import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Settings, Calendar, X, Check, Mail, MessageSquare, Users, Lightbulb, Loader2, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useThemeStore } from '../store/theme';
import { useAuthStore } from '../store/auth';
import { useWorkspaceStore } from '../store/workspace';

// Get base URL without /api suffix since we add it in fetch calls
const BASE_URL = (import.meta.env.VITE_API_URL || '').replace(/\/api\/?$/, '');

// Source options for daily summary
interface SourceOption {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
}

// Summary data structure
interface DailySummary {
  id: number;
  summary_date: string;
  social_media_summary: {
    content: string;
    postCount: number;
    engagementStats: Record<string, number>;
  } | null;
  email_summary: {
    content: string;
    emailCount: number;
    importantEmails: string[];
  } | null;
  employee_chat_summary: {
    content: string;
    conversationCount: number;
    topics: string[];
  } | null;
  calendar_summary: {
    content: string;
    eventCount: number;
    upcomingEvents: string[];
  } | null;
  conclusion: string | null;
  suggestions: string[] | null;
  status: string;
  generated_at: string | null;
}

// API Response types
interface SettingsResponse {
  success: boolean;
  data?: {
    id?: number;
    enabled: boolean;
    summary_time: string;
    selected_sources: string[];
  };
}

interface SummariesResponse {
  success: boolean;
  data?: DailySummary[];
}

// Gmail icon component
const GmailIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
  </svg>
);

// Google Calendar icon component
const GoogleCalendarIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path fill="#4285F4" d="M19.5 3.75h-15A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V6a2.25 2.25 0 0 0-2.25-2.25zM6 8.25h3v3H6v-3zm0 4.5h3v3H6v-3zm4.5-4.5h3v3h-3v-3zm0 4.5h3v3h-3v-3zm4.5-4.5h3v3h-3v-3zm0 4.5h3v3h-3v-3z"/>
    <path fill="#EA4335" d="M19.5 3.75h-15A2.25 2.25 0 0 0 2.25 6v1.5h19.5V6a2.25 2.25 0 0 0-2.25-2.25z"/>
  </svg>
);

// Social media icon
const SocialMediaIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6">
    <path fill="#1DA1F2" d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>
  </svg>
);

// Employee chat icon
const EmployeeChatIcon = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="#8B5CF6" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const sourceOptions: SourceOption[] = [
  { id: 'gmail', name: 'Gmail', icon: <GmailIcon />, color: 'bg-red-50' },
  { id: 'google-calendar', name: 'Google Calendar', icon: <GoogleCalendarIcon />, color: 'bg-blue-50' },
  { id: 'social-media', name: 'Social Media', icon: <SocialMediaIcon />, color: 'bg-sky-50' },
  { id: 'employee-chat', name: 'Employee Chat', icon: <EmployeeChatIcon />, color: 'bg-purple-50' },
];

// Generate time options in 30-minute intervals
const generateTimeOptions = (): string[] => {
  const times: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const h = hour % 12 || 12;
      const m = minute.toString().padStart(2, '0');
      const period = hour < 12 ? 'AM' : 'PM';
      times.push(`${h.toString().padStart(2, '0')}:${m} ${period}`);
    }
  }
  return times;
};

const timeOptions = generateTimeOptions();

export default function DailySummarizer() {
  const navigate = useNavigate();
  const { theme } = useThemeStore();
  const { token } = useAuthStore();
  const { currentWorkspace } = useWorkspaceStore();
  const isDark = theme === 'dark';

  const [showSettings, setShowSettings] = useState(false);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedTime, setSelectedTime] = useState('08:00 PM');
  const [tempSelectedTime, setTempSelectedTime] = useState('08:00 PM'); // Temporary time for picker
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [summaries, setSummaries] = useState<DailySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsEnabled, setSettingsEnabled] = useState(true);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Get workspace params
  const wsParam = currentWorkspace ? `?workspace_id=${currentWorkspace.id}` : '';
  const wsHeaders: Record<string, string> = currentWorkspace
    ? { 'X-Workspace-Id': String(currentWorkspace.id) }
    : {};

  // Fetch settings and summaries on load
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      setLoading(true);

      try {
        // Fetch settings
        const settingsRes = await fetch(`${BASE_URL}/api/daily-summarizer/settings${wsParam}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            ...wsHeaders,
          },
        });

        if (settingsRes.ok) {
          const settingsData: SettingsResponse = await settingsRes.json();
          if (settingsData.success && settingsData.data) {
            setSelectedSources(settingsData.data.selected_sources || []);
            setSelectedTime(settingsData.data.summary_time || '08:00 PM');
            setTempSelectedTime(settingsData.data.summary_time || '08:00 PM');
            setSettingsEnabled(settingsData.data.enabled !== false);
          }
        }

        // Fetch summaries
        const summariesRes = await fetch(`${BASE_URL}/api/daily-summarizer/summaries${wsParam}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            ...wsHeaders,
          },
        });

        if (summariesRes.ok) {
          const summariesData: SummariesResponse = await summariesRes.json();
          if (summariesData.success && summariesData.data) {
            setSummaries(summariesData.data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch daily summarizer data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, currentWorkspace?.id]);

  const toggleSource = (sourceId: string) => {
    setSelectedSources(prev =>
      prev.includes(sourceId)
        ? prev.filter(id => id !== sourceId)
        : [...prev, sourceId]
    );
  };

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);

    try {
      const res = await fetch(`${BASE_URL}/api/daily-summarizer/settings${wsParam}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          ...wsHeaders,
        },
        body: JSON.stringify({
          enabled: settingsEnabled,
          summary_time: selectedTime,
          selected_sources: selectedSources,
        }),
      });

      if (res.ok) {
        // Show success toast
        setToastMessage('Settings saved successfully!');
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
        setShowSettings(false);
      } else {
        console.error('Failed to save settings:', await res.text());
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Parse JSON safely
  const parseJSON = <T,>(str: string | null | undefined): T | null => {
    if (!str) return null;
    try {
      return JSON.parse(str) as T;
    } catch {
      return null;
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
        <Loader2 className={`w-8 h-8 animate-spin ${isDark ? 'text-white' : 'text-slate-700'}`} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/dashboard/automation')}
          className={`p-3 rounded-xl border ${
            isDark
              ? 'bg-dark-800 border-white/10 hover:bg-dark-700'
              : 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm'
          } transition-colors`}
        >
          <ArrowLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-slate-700'}`} />
        </button>

        <div className="flex items-center gap-2">
          {!showSettings && (
            <button
              onClick={() => setShowSettings(true)}
              className={`p-3 rounded-xl border ${
                isDark
                  ? 'bg-dark-800 border-white/10 hover:bg-dark-700'
                  : 'bg-white border-slate-200 hover:bg-slate-50 shadow-sm'
              } transition-colors`}
            >
              <Settings className={`w-5 h-5 ${isDark ? 'text-white' : 'text-slate-700'}`} />
            </button>
          )}
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pb-8">
        <AnimatePresence mode="wait">
          {showSettings ? (
            // Settings View
            <motion.div
              key="settings"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-8"
            >
              <h1 className={`text-2xl font-bold text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Daily Summarizer Settings
              </h1>

              {/* Enable/Disable Toggle */}
              <div className={`flex items-center justify-between p-4 rounded-xl border ${
                isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200'
              }`}>
                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-700'}`}>
                  Enable Daily Summarizer
                </span>
                <button
                  onClick={() => setSettingsEnabled(!settingsEnabled)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    settingsEnabled ? 'bg-blue-500' : isDark ? 'bg-dark-600' : 'bg-slate-300'
                  }`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                    settingsEnabled ? 'translate-x-6' : ''
                  }`} />
                </button>
              </div>

              {/* Source Selection */}
              <div>
                <h2 className={`text-base font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Select sources to summarize:
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {sourceOptions.map((source) => (
                    <button
                      key={source.id}
                      onClick={() => toggleSource(source.id)}
                      className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
                        selectedSources.includes(source.id)
                          ? isDark
                            ? 'bg-blue-500/20 border-blue-500'
                            : 'bg-blue-50 border-blue-500'
                          : isDark
                            ? 'bg-dark-800 border-white/10 hover:border-white/20'
                            : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex-shrink-0">
                        {source.icon}
                      </div>
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-700'}`}>
                        {source.name}
                      </span>
                      {selectedSources.includes(source.id) && (
                        <Check className="w-4 h-4 text-blue-500 ml-auto" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Selection */}
              <div>
                <h2 className={`text-base font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Daily summary at:
                </h2>
                <button
                  onClick={() => {
                    setTempSelectedTime(selectedTime); // Store current time
                    setShowTimePicker(true);
                  }}
                  className={`w-full flex items-center gap-3 p-4 rounded-xl border ${
                    isDark
                      ? 'bg-dark-800 border-white/10 hover:border-white/20'
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  } transition-colors`}
                >
                  <Calendar className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-700'}`}>
                    {selectedTime}
                  </span>
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSettings(false)}
                  className={`flex-1 py-4 rounded-xl font-medium border ${
                    isDark
                      ? 'bg-dark-700 border-white/10 text-white hover:bg-dark-600'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                  } transition-colors`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-4 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save
                </button>
              </div>
            </motion.div>
          ) : (
            // Main View (Summaries or Empty State)
            <motion.div
              key="main"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-6"
            >
              <h1 className={`text-2xl font-bold text-center ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Daily Summarizer
              </h1>

              {summaries.length === 0 ? (
                // Empty State
                <div className="flex flex-col items-center justify-center py-12">
                  {/* Character Illustration */}
                  <div className="w-48 h-48 mb-8">
                    <img
                      src="/images/daily-summarizer-mascot.png"
                      alt="Daily Summarizer"
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = '<div class="w-48 h-48 flex items-center justify-center text-8xl">📋</div>';
                        }
                      }}
                    />
                  </div>

                  {/* Empty State Card */}
                  <div className={`w-full max-w-md p-8 rounded-2xl border text-center ${
                    isDark
                      ? 'bg-dark-800 border-white/10'
                      : 'bg-white border-slate-200 shadow-sm'
                  }`}>
                    {/* Box Icon */}
                    <div className="flex justify-center mb-4">
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                        isDark ? 'bg-purple-500/20' : 'bg-purple-100'
                      }`}>
                        <svg className="w-8 h-8 text-purple-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4" />
                        </svg>
                      </div>
                    </div>

                    <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      No summaries available
                    </h3>
                    <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                      Your daily summaries will appear here automatically at your scheduled time.
                    </p>
                    <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                      Configure your sources and time in settings.
                    </p>
                  </div>
                </div>
              ) : (
                // Summaries List
                <div className="space-y-6">
                  {summaries.map((summary) => {
                    const socialMedia = typeof summary.social_media_summary === 'string'
                      ? parseJSON<DailySummary['social_media_summary']>(summary.social_media_summary)
                      : summary.social_media_summary;
                    const email = typeof summary.email_summary === 'string'
                      ? parseJSON<DailySummary['email_summary']>(summary.email_summary)
                      : summary.email_summary;
                    const employeeChat = typeof summary.employee_chat_summary === 'string'
                      ? parseJSON<DailySummary['employee_chat_summary']>(summary.employee_chat_summary)
                      : summary.employee_chat_summary;
                    const calendar = typeof summary.calendar_summary === 'string'
                      ? parseJSON<DailySummary['calendar_summary']>(summary.calendar_summary)
                      : summary.calendar_summary;
                    const suggestions = typeof summary.suggestions === 'string'
                      ? parseJSON<string[]>(summary.suggestions)
                      : summary.suggestions;

                    return (
                      <div
                        key={summary.id}
                        className={`rounded-2xl border overflow-hidden ${
                          isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200 shadow-sm'
                        }`}
                      >
                        {/* Summary Header */}
                        <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
                          <div className="flex items-center justify-between">
                            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {formatDate(summary.summary_date)}
                            </h3>
                            {summary.status === 'generating' && (
                              <span className="flex items-center gap-1 text-xs text-amber-500">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                Generating...
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Summary Sections */}
                        <div className="p-4 space-y-4">
                          {/* Social Media Summary */}
                          {socialMedia && (
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`p-1.5 rounded-lg ${isDark ? 'bg-sky-500/20' : 'bg-sky-100'}`}>
                                  <SocialMediaIcon />
                                </div>
                                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                  Social Media
                                </h4>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  isDark ? 'bg-sky-500/20 text-sky-400' : 'bg-sky-100 text-sky-600'
                                }`}>
                                  {socialMedia.postCount} posts
                                </span>
                              </div>
                              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                                {socialMedia.content}
                              </p>
                            </div>
                          )}

                          {/* Email Summary */}
                          {email && (
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`p-1.5 rounded-lg ${isDark ? 'bg-red-500/20' : 'bg-red-100'}`}>
                                  <Mail className="w-5 h-5 text-red-500" />
                                </div>
                                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                  Email
                                </h4>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600'
                                }`}>
                                  {email.emailCount} emails
                                </span>
                              </div>
                              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                                {email.content}
                              </p>
                            </div>
                          )}

                          {/* Employee Chat Summary */}
                          {employeeChat && (
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`p-1.5 rounded-lg ${isDark ? 'bg-purple-500/20' : 'bg-purple-100'}`}>
                                  <Users className="w-5 h-5 text-purple-500" />
                                </div>
                                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                  Employee Conversations
                                </h4>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                                }`}>
                                  {employeeChat.conversationCount} chats
                                </span>
                              </div>
                              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                                {employeeChat.content}
                              </p>
                              {employeeChat.topics && employeeChat.topics.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {employeeChat.topics.map((topic, i) => (
                                    <span
                                      key={i}
                                      className={`text-xs px-2 py-0.5 rounded-full ${
                                        isDark ? 'bg-dark-600 text-gray-400' : 'bg-slate-200 text-slate-600'
                                      }`}
                                    >
                                      {topic}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Calendar Summary */}
                          {calendar && (
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <div className={`p-1.5 rounded-lg ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                                  <Calendar className="w-5 h-5 text-blue-500" />
                                </div>
                                <h4 className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                  Calendar
                                </h4>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'
                                }`}>
                                  {calendar.eventCount} events
                                </span>
                              </div>
                              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                                {calendar.content}
                              </p>
                            </div>
                          )}

                          {/* Conclusion */}
                          {summary.conclusion && (
                            <div className={`p-4 rounded-xl border-l-4 border-amber-500 ${
                              isDark ? 'bg-amber-500/10' : 'bg-amber-50'
                            }`}>
                              <div className="flex items-center gap-2 mb-2">
                                <MessageSquare className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                                <h4 className={`font-medium ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                                  Conclusion
                                </h4>
                              </div>
                              <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                                {summary.conclusion}
                              </p>
                            </div>
                          )}

                          {/* Suggestions */}
                          {suggestions && suggestions.length > 0 && (
                            <div className={`p-4 rounded-xl border-l-4 border-green-500 ${
                              isDark ? 'bg-green-500/10' : 'bg-green-50'
                            }`}>
                              <div className="flex items-center gap-2 mb-2">
                                <Lightbulb className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                                <h4 className={`font-medium ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                                  Suggestions
                                </h4>
                              </div>
                              <ul className={`text-sm space-y-1 ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                                {suggestions.map((suggestion, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <span className="text-green-500 mt-1">•</span>
                                    {suggestion}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Time Picker Modal */}
      <AnimatePresence>
        {showTimePicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setTempSelectedTime(selectedTime); // Revert to original on backdrop click
              setShowTimePicker(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-sm rounded-2xl p-6 ${
                isDark ? 'bg-dark-800' : 'bg-white'
              }`}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Select Time
                </h3>
                <button
                  onClick={() => {
                    setTempSelectedTime(selectedTime); // Revert to original
                    setShowTimePicker(false);
                  }}
                  className={`p-2 rounded-lg ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                >
                  <X className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-slate-500'}`} />
                </button>
              </div>

              {/* Time Options Scrollable List */}
              <div className="relative">
                {/* Scroll indicators */}
                <div className={`absolute top-0 left-0 right-0 h-8 pointer-events-none z-10 ${
                  isDark
                    ? 'bg-gradient-to-b from-dark-800 to-transparent'
                    : 'bg-gradient-to-b from-white to-transparent'
                }`} />
                <div className={`absolute bottom-0 left-0 right-0 h-8 pointer-events-none z-10 ${
                  isDark
                    ? 'bg-gradient-to-t from-dark-800 to-transparent'
                    : 'bg-gradient-to-t from-white to-transparent'
                }`} />

                <div className="max-h-64 overflow-y-auto scrollbar-hide">
                  <div className="py-4 space-y-1">
                    {timeOptions.map((time) => (
                      <button
                        key={time}
                        onClick={() => {
                          setTempSelectedTime(time);
                        }}
                        className={`w-full py-3 px-4 rounded-xl text-center transition-colors ${
                          tempSelectedTime === time
                            ? 'bg-blue-500/20 text-blue-500 font-medium'
                            : isDark
                              ? 'text-white hover:bg-dark-700'
                              : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setTempSelectedTime(selectedTime); // Revert to original
                    setShowTimePicker(false);
                  }}
                  className={`flex-1 py-3 rounded-xl font-medium border ${
                    isDark
                      ? 'bg-dark-700 border-white/10 text-white hover:bg-dark-600'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                  } transition-colors`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setSelectedTime(tempSelectedTime); // Apply the selection
                    setShowTimePicker(false);
                  }}
                  className="flex-1 py-3 rounded-xl font-medium bg-blue-500 hover:bg-blue-600 text-white transition-colors"
                >
                  Confirm
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Success Toast */}
      <AnimatePresence>
        {showSuccessToast && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
          >
            <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg ${
              isDark ? 'bg-green-600 text-white' : 'bg-green-500 text-white'
            }`}>
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">{toastMessage}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
